import { z } from 'zod';
/*
 * Журнал запросов «Запуски FB» (спека «Конструктор FB v4» §9.5, §10.2):
 * `GET /api/ads/request-log`. Одна строка — одна попытка действия человека
 * в админке (`source: 'admin'`) или нейронки через MCP (`'mcp'`). Ни текстов,
 * ни тел, ни e-mail: «Что» и «Почему» экран строит по id/кодам.
 */
export const ADS_REQUEST_LOG_OUTCOME_VALUES = [
    'ok',
    'rejected',
    'forbidden',
    'invalid',
    'unavailable',
    'unknown',
    'error',
];
export const AdsRequestLogOutcome = z.enum(ADS_REQUEST_LOG_OUTCOME_VALUES);
export const AdsRequestLogSource = z.enum(['admin', 'mcp']);
export const AdsRequestLogStoppedAt = z.enum(['admin', 'mcp', 'auto_ads', 'meta']);
/** Максимальный размер страницы журнала. */
export const ADS_REQUEST_LOG_MAX_LIMIT = 100;
const IsoDate = z.string().refine((v) => !Number.isNaN(Date.parse(v)), {
    message: 'Дата в формате ISO',
});
/**
 * Фильтры журнала. `mine=1` — только свои (по умолчанию на экране);
 * `outcome=failed` — все неуспешные («Только ошибки»). Время — UTC ISO.
 */
export const AdsRequestLogQuery = z
    .object({
    from: IsoDate.optional(),
    to: IsoDate.optional(),
    adminId: z.string().trim().min(1).max(100).optional(),
    mine: z.enum(['1', '0']).optional(),
    outcome: z.union([AdsRequestLogOutcome, z.literal('failed')]).optional(),
    source: AdsRequestLogSource.optional(),
    operation: z
        .string()
        .regex(/^[A-Za-z0-9_]{1,100}$/)
        .optional(),
    resourceId: z
        .string()
        .regex(/^[A-Za-z0-9]{1,64}$/)
        .optional(),
    requestId: z.string().trim().min(1).max(100).optional(),
    cursor: z.string().min(1).max(200).optional(),
    limit: z.coerce.number().int().min(1).max(ADS_REQUEST_LOG_MAX_LIMIT).optional(),
})
    .strict();
/** Замечание строки: только машинные поля, текст экран строит по коду. */
export const AdsRequestLogIssue = z.object({
    code: z.string(),
    severity: z.enum(['error', 'warning']),
    fieldKey: z.string().optional(),
    groupKey: z.string().optional(),
});
/**
 * «Что» — название объекта по `resourceId` (для preview — шаблон из
 * `summary.templateId`), собранное admin-api на странице одним проходом.
 * `name: null` — объект не найден / сервис рекламы не ответил. У пачки —
 * живое состояние (`state`, число запусков и запусков с ошибкой).
 */
export const AdsRequestLogWhat = z.object({
    kind: z.enum(['template', 'batch', 'launch', 'account', 'other']),
    id: z.string(),
    name: z.string().nullable(),
    /** Пачка: когда создана (имени у пачки нет — «пачка от 26.10 11:02»). */
    createdAt: z.string().nullable().default(null),
    batch: z
        .object({
        state: z.string(),
        launches: z.number().int(),
        failed: z.number().int(),
    })
        .nullable()
        .default(null),
});
export const AdsRequestLogRow = z.object({
    id: z.string(),
    at: z.string(),
    requestId: z.string(),
    /** `X-Request-Id` клиента (если присылал) — только для сопоставления. */
    clientRequestId: z.string().nullable(),
    source: AdsRequestLogSource,
    adminId: z.string().nullable(),
    /** Имя админа (join), не e-mail; null — админ удалён / не найден. */
    adminName: z.string().nullable(),
    operation: z.string(),
    method: z.string(),
    /** Шаблон маршрута admin-api / имя инструмента MCP — что сделал человек. */
    route: z.string().nullable(),
    /** Шаблон пути auto-ads; null — до сервиса рекламы не дошли. */
    upstreamRoute: z.string().nullable(),
    resourceId: z.string().nullable(),
    httpStatus: z.number().int().nullable(),
    upstreamStatus: z.number().int().nullable(),
    outcome: AdsRequestLogOutcome,
    errorCode: z.string().nullable(),
    stoppedAt: AdsRequestLogStoppedAt.nullable(),
    issuesCount: z.number().int().nullable(),
    issues: z.array(AdsRequestLogIssue).nullable(),
    summary: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).nullable(),
    durationMs: z.number().int().nullable(),
    what: AdsRequestLogWhat.nullable(),
});
export const AdsRequestLogResponse = z.object({
    items: z.array(AdsRequestLogRow),
    nextCursor: z.string().nullable(),
    /** Самая ранняя строка журнала («Журнал ведётся с …»); null — пусто. */
    since: z.string().nullable(),
});
//# sourceMappingURL=ads-request-log.js.map